import React, { FC, useEffect, useRef, useState } from 'react';

import { Row } from 'antd';
import ReactPlayer from 'react-player';
import {
  extractVideoID,
  isValidUrl,
  VideoTypesEnum,
} from 'src/components/staticComponents/Video';
import {
  IChallengeResourceRender,
  IChallengeVideoSection,
} from 'src/shared/models';
import { useWindowSize } from 'utils';

interface OwnProps extends IChallengeResourceRender {
  config: Partial<IChallengeVideoSection>;
}

const ChallengeVideoSection: FC<OwnProps> = ({ config }) => {
  const { id, imgDesktop, imgMobile, title, type, description, vertical } =
    config;

  return (
    <VideoDetail
      customClassName="video-section"
      description={description}
      id={id}
      imgDesktop={imgDesktop}
      imgMobile={imgMobile}
      title={title}
      type={type}
      vertical={vertical}
    />
  );
};

export default ChallengeVideoSection;

//TODO extract this in a component and unify with VIDEO SECTION from STATIC PAGES
interface IVideoDetail {
  config?: any;
  controls?: boolean;
  customClassName?: string;
  description?: string;
  id: string;
  imgDesktop?: string;
  imgMobile?: string;
  loop?: boolean;
  title: string;
  type: number;
  vertical?: boolean;
}

export const VideoDetail: FC<IVideoDetail> = ({
  config,
  controls,
  description,
  id,
  imgDesktop,
  imgMobile,
  loop,
  title,
  type,
  vertical,
}) => {
  const defaultHeight = '100%';
  const [playing, setPlaying] = useState(false);
  const [playerHeight, setPlayerHeight] = useState(defaultHeight);
  const videoRef = useRef();

  const { isMD, isSM } = useWindowSize();

  const controllable = controls !== undefined ? controls : true;
  const imageDesktop = imgDesktop || null;
  const imageMobile = imgMobile || null;
  const isAutoplay = String(type) === VideoTypesEnum.AUTOPLAY;
  const isLight = String(type) === VideoTypesEnum.LIGHT;

  const isYoutube =
    (isValidUrl(id) && extractVideoID(id)) || (id && !isValidUrl(id));
  const videoId = isYoutube ? (isValidUrl(id) ? extractVideoID(id) : id) : null;

  const youtubeVideoPlaceholder =
    (videoId && `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`) ||
    null;

  const url = isYoutube ? `https://www.youtube.com/watch?v=${videoId}` : id;

  const reactPlayerClass = vertical
    ? 'react-player vertical-video'
    : 'react-player';

  let placeholderImage: string;

  switch (true) {
    case isMD && Boolean(imageMobile):
      placeholderImage = imageMobile;
      break;
    case Boolean(imageDesktop):
      placeholderImage = imageDesktop;
      break;
    default:
      placeholderImage = youtubeVideoPlaceholder;
  }

  useEffect(() => {
    /**
     * Autoplay may only work only when either the media is muted, or after
     * the user clicks or taps on the site, or (on desktop) if the user has
     * previously shown an interest in media on the site.
     * https://blog.chromium.org/2018/03/chrome-66-beta-css-typed-object-model.html
     */

    isAutoplay && setPlaying(true);
  }, [isAutoplay]);

  useEffect(() => {
    if (!vertical) return;

    const handleResize = () => {
      const width = window.innerWidth;
      const height = isSM ? width * (16 / 9) + 'px' : '867px'; //width * (16/9) + 'px'; // 9:16 aspect ratio

      setPlayerHeight(height);
    };

    if (typeof window !== 'undefined') {
      handleResize();
      // Add event listener to handle window resize
      window.addEventListener('resize', handleResize);

      // cleanup event listener
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [vertical, isMD, defaultHeight]);

  const handleIsLightPlay = () => {
    setPlaying(true);
  };

  return (
    <>
      <Row
        justify="center"
        className="video--detail-row experience-headboard__details--media video--detail-player">
        <div
          className={`video--detail-player--wrapper ${
            vertical ? 'video--detail-player--wrapper--vertical' : ''
          }`}>
            
          <ReactPlayer
            className={reactPlayerClass}
            config={config}
            controls={controllable}
            height={playerHeight}
            light={isLight && placeholderImage}
            loop={loop}
            muted={isAutoplay}
            onClickPreview={handleIsLightPlay}
            playIcon={<React.Fragment />}
            playing={playing}
            playsinline={true}
            ref={videoRef}
            url={url}
            width="100%"
          />
        </div>
      </Row>
      {(title || description) && (
        <Row
          justify="center"
          className="video--detail-row experience-headboard__details--media video--detail-text">
          <div className="video--detail-text--wrapper">
            {title && <h3>{title}</h3>}
            <p>{description}</p>
          </div>
        </Row>
      )}
    </>
  );
};
